import { useLayoutEffect, useRef, useState } from "react";

/** Below this tab width the list and the pane no longer sit side by side. */
export const SPLIT_MIN_PX = 1000;

export function splitFor(width: number): boolean {
  return width >= SPLIT_MIN_PX;
}

/**
 * Whether the tab has room for the pane beside the list. The TAB's own width, not the
 * viewport's: the shell draws a rail beside every module, so a viewport breakpoint
 * cannot know how much room this tab really has (same reason as `useFactsLayout`).
 * With nothing to measure (a server render, a test) it assumes room: the pane renders
 * inline, which degrades to a longer page rather than to a request that cannot be opened.
 *
 * Its own module, like `useFactsLayout`, so the screen harness can hand it the width the
 * tab would measure: a layout effect never runs under a static render.
 */
export function useInboxLayout<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [split, setSplit] = useState(true);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const sync = () => setSplit(splitFor(el.getBoundingClientRect().width));
    sync();
    if (typeof ResizeObserver !== "function") return;
    const observer = new ResizeObserver(sync);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return { ref, split };
}
